import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { matchPayroll, PayrollMatchSummary } from '../../api/batch4Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the Payroll workbook (First Name, Last Name, Final Pay) and the Net Wages / Account Transactions workbook (Date, Description, Debit/Credit).',
  'Set the minimum fuzzy name-match score. Lower catches more name variants but risks coincidental matches; higher is stricter.',
  'Click Run Payroll Match. Matched_Wages.xlsx downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function PayrollMatchPanel({ mission, onBack }: PanelProps) {
  const [payroll, setPayroll] = useState<File[]>([]);
  const [netWages, setNetWages] = useState<File[]>([]);
  const [threshold, setThreshold] = useState(55);
  const { status, error, result, fail, run } = useFileRun<FileResult<PayrollMatchSummary>>();

  const handleSubmit = async () => {
    if (payroll.length === 0 || netWages.length === 0) {
      fail('Upload both the payroll and the net wages files.');
      return;
    }
    await run(() => matchPayroll(payroll[0], netWages[0], threshold), 'Matching failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="grid gap-4 sm:grid-cols-2">
        <FileDrop
          label="Payroll"
          required
          accept={mission.accept}
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          files={payroll}
          onFiles={setPayroll}
        />
        <FileDrop
          label="Net Wages / Account Transactions"
          required
          accept={mission.accept}
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          files={netWages}
          onFiles={setNetWages}
        />
      </div>

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="pm-threshold" className="mb-1.5">
          Minimum fuzzy name-match score: {threshold}
        </Label>
        <input
          id="pm-threshold"
          type="range"
          min={0}
          max={100}
          value={threshold}
          onChange={(e) => setThreshold(Number(e.target.value))}
          className="w-full"
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Matching…' : 'Run Payroll Match'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Matching…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && s && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {result.filename} downloaded.</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ['Exact match', s.exact],
              ['Not exact match', s.not_exact],
              ['Unmatched', s.unmatched],
              ['Unreconciled', s.unreconciled],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-border bg-muted px-3 py-2 text-center">
                <div className="text-xl font-bold text-foreground">{value}</div>
                <div className="text-xs text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>
          {s.unreconciled > 0 && (
            <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
              {s.unreconciled} bank transaction(s) either never cleared the match threshold or were coincidentally
              assigned to a payroll name whose text they don't contain. Check the Unreconciled Transactions sheet before
              relying on the totals.
            </div>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload both files above to enable matching.</InfoNotice>}
    </MissionShell>
  );
}

export default PayrollMatchPanel;
